/**
 * VISTOERA_AI_CHAT_CONTROL_PLANE_P0 — Platform Admin
 * "AI 管理 → AI 对话任务" page.
 *
 * This is purely an observability + control surface over the *existing*
 * Tenant AI Chat data model (`AIChatConversation` / `AIChatMessage` /
 * `AIChatExecution` + the generic `Job`/`Worker` Task Contract). No
 * shadow task table is created anywhere in this file — every row on
 * screen is a straight backend join (`ai_chat_admin_service.py`) over
 * those tables, and every action (Cancel/Retry) calls the exact same
 * `JobService`/`ai_chat_service.retry_execution` contract the Tenant
 * chat surface itself uses.
 *
 * Two views share one Filter Panel + a right-side detail Drawer:
 *   - Task List: one row per AIChatExecution (flat audit trail — a
 *     superseded execution from a previous Retry stays visible here,
 *     spec's own "not in -> in" decision, see backend tests).
 *   - Conversation Aggregate: one row per Message, expandable into its
 *     up-to-3 sibling Executions ("2/3 完成 · 1 失败" style label).
 *
 * All filter/sort/page state lives in the URL query string (spec §61 —
 * a Platform Admin must be able to bookmark/share/refresh a filtered
 * view without losing it).
 */
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import type { TableColumnsType } from 'antd'
import { Button, DatePicker, Empty, Input, Segmented, Select, Table, Tag, Tooltip, message } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  platformCancelAIChatExecution,
  platformGetAIChatExecutionDetail,
  platformGetAIChatMetrics,
  platformListAIChatConversationAggregate,
  platformListAIChatExecutions,
  platformListWorkers,
  platformListWorkspaces,
  platformRetryAIChatExecution,
} from '../api'
import type {
  AIChatAdminConversationAggregateRow,
  AIChatAdminExecutionDetail,
  AIChatAdminExecutionRow,
  AIChatAdminMetrics,
  AdminWorkerRow,
  WorkspaceWithCounts,
} from '../types'
import { ProviderBadge, StatusBadge, SummaryCard, formatDateTime } from './adminShared'
import AIChatExecutionDetailDrawer from './AIChatExecutionDetailDrawer'
import AIChatTaskListTable from './AIChatTaskListTable'
import { cardIconBg, platformColors } from './theme'

const PROVIDER_OPTIONS = [
  { value: '', label: '全部服务商' },
  { value: 'chatgpt', label: 'ChatGPT' },
  { value: 'gemini', label: 'Gemini' },
  { value: 'grok', label: 'Grok' },
  { value: 'qwen', label: '通义千问 (legacy)' },
  { value: 'deepseek', label: 'DeepSeek (legacy)' },
]

const STATUS_OPTIONS = [
  { value: '', label: '全部状态' },
  { value: 'queued', label: '排队中' },
  { value: 'claimed', label: '已认领' },
  { value: 'running', label: '运行中' },
  { value: 'succeeded', label: '已完成' },
  { value: 'failed', label: '失败' },
  { value: 'cancelled', label: '已取消' },
]

const ACTIVE_STATUSES = new Set(['queued', 'claimed', 'running'])

function durationLabel(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—'
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const minutes = Math.floor(seconds / 60)
  const rest = Math.round(seconds % 60)
  return `${minutes}m${rest}s`
}

function copyToClipboard(text: string) {
  navigator.clipboard
    ?.writeText(text)
    .then(() => message.success('已复制'))
    .catch(() => message.error('复制失败'))
}

function IdCell({ id }: { id: string }) {
  return (
    <Tooltip title={`${id}（点击复制）`}>
      <span
        style={{ fontFamily: 'monospace', fontSize: 12, cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation()
          copyToClipboard(id)
        }}
      >
        {id.slice(0, 8)}
      </span>
    </Tooltip>
  )
}

interface Filters {
  provider: string
  status: string
  workerId: string
  workspaceId: string
  keyword: string
  dateFrom: string
  dateTo: string
}

function filtersFromParams(params: URLSearchParams): Filters {
  return {
    provider: params.get('provider') || '',
    status: params.get('status') || '',
    workerId: params.get('worker_id') || '',
    workspaceId: params.get('workspace_id') || '',
    keyword: params.get('keyword') || '',
    dateFrom: params.get('date_from') || '',
    dateTo: params.get('date_to') || '',
  }
}

export default function AIChatTasksPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const view = (searchParams.get('view') === 'aggregate' ? 'aggregate' : 'list') as 'list' | 'aggregate'
  const page = Number(searchParams.get('page') || '1') || 1
  const pageSize = Number(searchParams.get('page_size') || '20') || 20
  const sort = searchParams.get('sort') || 'created_at'
  const sortDir = (searchParams.get('sort_dir') === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc'
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams])
  const selectedExecutionId = searchParams.get('execution_id') || null

  const [draftKeyword, setDraftKeyword] = useState(filters.keyword)
  useEffect(() => setDraftKeyword(filters.keyword), [filters.keyword])

  const patchParams = useCallback(
    (patch: Record<string, string | null>, opts?: { resetPage?: boolean }) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === '') next.delete(k)
          else next.set(k, v)
        }
        if (opts?.resetPage !== false) next.delete('page')
        return next
      })
    },
    [setSearchParams],
  )

  const [workers, setWorkers] = useState<AdminWorkerRow[]>([])
  const [workspaces, setWorkspaces] = useState<WorkspaceWithCounts[]>([])
  useEffect(() => {
    platformListWorkers().then((r) => setWorkers(r.data.workers)).catch(() => {})
    // VISTOERA_TENANT_ADMIN_360_..._P0_V1 — the list route now returns a
    // paginated {items, total, summary} envelope; this filter dropdown
    // wants "every workspace", so request a generously large page_size
    // rather than adding a second, unpaginated list endpoint.
    platformListWorkspaces({ page_size: 500 })
      .then((r) => setWorkspaces(r.data.items))
      .catch(() => {})
  }, [])

  const workerOptions = useMemo(
    () => [{ value: '', label: '全部 Worker' }, ...workers.map((w) => ({ value: w.worker_id, label: w.name || w.worker_id }))],
    [workers],
  )
  const workspaceOptions = useMemo(
    () => [{ value: '', label: '全部租户' }, ...workspaces.map((w) => ({ value: w.id, label: w.name }))],
    [workspaces],
  )

  const filterQuery = useMemo(
    () => ({
      provider: filters.provider || undefined,
      status: filters.status || undefined,
      worker_id: filters.workerId || undefined,
      workspace_id: filters.workspaceId || undefined,
      date_from: filters.dateFrom || undefined,
      date_to: filters.dateTo || undefined,
      keyword: filters.keyword || undefined,
    }),
    [filters],
  )

  // ---------------------------------------------------------------- KPIs
  const [metrics, setMetrics] = useState<AIChatAdminMetrics | null>(null)
  const [metricsLoading, setMetricsLoading] = useState(true)
  const loadMetrics = useCallback(() => {
    setMetricsLoading(true)
    platformGetAIChatMetrics(filterQuery)
      .then((r) => setMetrics(r.data))
      .catch(() => setMetrics(null))
      .finally(() => setMetricsLoading(false))
  }, [filterQuery])

  // ------------------------------------------------------------ Task List
  const [listData, setListData] = useState<{ items: AIChatAdminExecutionRow[]; total: number } | null>(null)
  const [listLoading, setListLoading] = useState(true)
  const [listError, setListError] = useState<string | null>(null)
  const loadList = useCallback(
    (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setListLoading(true)
      platformListAIChatExecutions({ page, page_size: pageSize, sort, sort_dir: sortDir, ...filterQuery })
        .then((r) => {
          setListData({ items: r.data.items, total: r.data.total })
          setListError(null)
        })
        .catch((e) => setListError(e?.response?.data?.detail?.message ?? e?.message ?? '加载失败'))
        .finally(() => setListLoading(false))
    },
    [page, pageSize, sort, sortDir, filterQuery],
  )

  // ------------------------------------------------------- Conversation Aggregate
  const [aggData, setAggData] = useState<{ items: AIChatAdminConversationAggregateRow[]; total: number } | null>(null)
  const [aggLoading, setAggLoading] = useState(true)
  const [aggError, setAggError] = useState<string | null>(null)
  const loadAgg = useCallback(
    (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setAggLoading(true)
      platformListAIChatConversationAggregate({
        page,
        page_size: pageSize,
        workspace_id: filterQuery.workspace_id,
        date_from: filterQuery.date_from,
        date_to: filterQuery.date_to,
        keyword: filterQuery.keyword,
      })
        .then((r) => {
          setAggData({ items: r.data.items, total: r.data.total })
          setAggError(null)
        })
        .catch((e) => setAggError(e?.response?.data?.detail?.message ?? e?.message ?? '加载失败'))
        .finally(() => setAggLoading(false))
    },
    [page, pageSize, filterQuery],
  )

  const reloadAll = useCallback(
    (opts?: { silent?: boolean }) => {
      loadMetrics()
      if (view === 'list') loadList(opts)
      else loadAgg(opts)
    },
    [view, loadMetrics, loadList, loadAgg],
  )

  useEffect(() => {
    reloadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, page, pageSize, sort, sortDir, filterQuery])

  // Conditional polling — only while the *currently visible* page has an
  // in-flight (queued/claimed/running) row, so an Admin looking at a page
  // full of terminal rows never sees the table silently re-render under
  // their cursor (spec §63).
  const hasActive = useMemo(() => {
    if (view === 'list') return (listData?.items ?? []).some((r) => ACTIVE_STATUSES.has(r.status))
    return (aggData?.items ?? []).some((row) => row.executions.some((e) => ACTIVE_STATUSES.has(e.status)))
  }, [view, listData, aggData])

  useEffect(() => {
    if (!hasActive) return
    const timer = window.setInterval(() => reloadAll({ silent: true }), 8000)
    return () => window.clearInterval(timer)
  }, [hasActive, reloadAll])

  // ------------------------------------------------------------- Detail drawer
  const [execDetail, setExecDetail] = useState<AIChatAdminExecutionDetail | null>(null)
  const [execDetailLoading, setExecDetailLoading] = useState(false)
  const [execDetailError, setExecDetailError] = useState<string | null>(null)
  const [actionBusy, setActionBusy] = useState(false)
  const loadSeqRef = useRef(0)

  const loadExecDetail = useCallback((executionId: string) => {
    const seq = ++loadSeqRef.current
    setExecDetailLoading(true)
    setExecDetailError(null)
    setExecDetail(null)
    platformGetAIChatExecutionDetail(executionId)
      .then((r) => {
        if (seq !== loadSeqRef.current) return
        setExecDetail(r.data)
      })
      .catch((e) => {
        if (seq !== loadSeqRef.current) return
        setExecDetail(null)
        setExecDetailError(e?.response?.data?.detail?.message ?? e?.response?.data?.message ?? e?.message ?? '加载失败')
      })
      .finally(() => {
        if (seq === loadSeqRef.current) setExecDetailLoading(false)
      })
  }, [])

  useEffect(() => {
    if (selectedExecutionId) loadExecDetail(selectedExecutionId)
    else {
      loadSeqRef.current += 1
      setExecDetail(null)
      setExecDetailError(null)
      setExecDetailLoading(false)
    }
  }, [selectedExecutionId, loadExecDetail])

  const closeDetailDrawer = useCallback(() => {
    patchParams({ execution_id: null }, { resetPage: false })
  }, [patchParams])

  const selectRow = useCallback(
    (executionId: string) => {
      patchParams({ execution_id: executionId }, { resetPage: false })
    },
    [patchParams],
  )

  const handleCancel = useCallback(
    (executionId: string) => {
      setActionBusy(true)
      platformCancelAIChatExecution(executionId)
        .then(() => {
          message.success('已取消')
          reloadAll({ silent: true })
          if (selectedExecutionId === executionId) loadExecDetail(executionId)
        })
        .catch((e) => message.error(e?.response?.data?.detail?.message ?? '取消失败'))
        .finally(() => setActionBusy(false))
    },
    [reloadAll, selectedExecutionId, loadExecDetail],
  )

  const handleRetry = useCallback(
    (executionId: string) => {
      setActionBusy(true)
      platformRetryAIChatExecution(executionId)
        .then((r) => {
          message.success('已创建重试任务')
          reloadAll({ silent: true })
          selectRow(r.data.execution_id)
        })
        .catch((e) => message.error(e?.response?.data?.detail?.message ?? '重试失败'))
        .finally(() => setActionBusy(false))
    },
    [reloadAll, selectRow],
  )

  const handleTablePageChange = useCallback(
    (patch: Record<string, string | null>) => {
      patchParams(patch, { resetPage: false })
    },
    [patchParams],
  )

  // ---------------------------------------------------------------- Render
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 18, color: platformColors.primaryText }}>AI 对话任务</h2>
        <Button icon={<ReloadOutlined />} onClick={() => reloadAll()}>
          刷新
        </Button>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <SummaryCard
          title="总任务数"
          value={metrics?.total_tasks ?? '—'}
          loading={metricsLoading}
          iconBg={cardIconBg.task}
          icon={<ThunderboltOutlined style={{ color: platformColors.primary }} />}
        />
        <SummaryCard
          title="运行中"
          value={metrics?.running ?? '—'}
          loading={metricsLoading}
          iconBg={platformColors.infoBg}
          icon={<PlayCircleOutlined style={{ color: platformColors.info }} />}
        />
        <SummaryCard
          title="已完成"
          value={metrics?.succeeded ?? '—'}
          loading={metricsLoading}
          iconBg={platformColors.successBg}
          icon={<CheckCircleOutlined style={{ color: platformColors.success }} />}
        />
        <SummaryCard
          title="失败"
          value={metrics?.failed ?? '—'}
          loading={metricsLoading}
          iconBg={platformColors.dangerBg}
          icon={<CloseCircleOutlined style={{ color: platformColors.danger }} />}
        />
        <SummaryCard
          title="平均执行耗时"
          value={durationLabel(metrics?.avg_execution_duration_seconds ?? null)}
          loading={metricsLoading}
          iconBg={platformColors.warningBg}
          icon={<ClockCircleOutlined style={{ color: platformColors.warning }} />}
        />
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexWrap: 'wrap',
          background: platformColors.surface,
          border: `1px solid ${platformColors.border}`,
          borderRadius: 8,
          padding: 10,
        }}
      >
        <Segmented
          value={view}
          onChange={(v) => patchParams({ view: v === 'aggregate' ? 'aggregate' : null, execution_id: null })}
          options={[
            { label: '任务列表', value: 'list' },
            { label: '会话聚合', value: 'aggregate' },
          ]}
        />
        <DatePicker.RangePicker
          size="small"
          value={filters.dateFrom && filters.dateTo ? [dayjs(filters.dateFrom), dayjs(filters.dateTo)] : null}
          onChange={(v) =>
            patchParams({
              date_from: v?.[0] ? v[0].startOf('day').toISOString() : null,
              date_to: v?.[1] ? v[1].endOf('day').toISOString() : null,
            })
          }
        />
        {view === 'list' && (
          <Select size="small" style={{ width: 140 }} value={filters.provider || ''} options={PROVIDER_OPTIONS} onChange={(v) => patchParams({ provider: v || null })} />
        )}
        {view === 'list' && (
          <Select size="small" style={{ width: 130 }} value={filters.status || ''} options={STATUS_OPTIONS} onChange={(v) => patchParams({ status: v || null })} />
        )}
        <Select
          size="small"
          style={{ width: 160 }}
          value={filters.workerId || ''}
          options={workerOptions}
          showSearch
          optionFilterProp="label"
          onChange={(v) => patchParams({ worker_id: v || null })}
        />
        <Select
          size="small"
          style={{ width: 160 }}
          value={filters.workspaceId || ''}
          options={workspaceOptions}
          showSearch
          optionFilterProp="label"
          onChange={(v) => patchParams({ workspace_id: v || null })}
        />
        <Input.Search
          size="small"
          style={{ width: 220 }}
          placeholder="Task/Execution/Conversation/Message ID"
          value={draftKeyword}
          onChange={(e) => setDraftKeyword(e.target.value)}
          onSearch={(v) => patchParams({ keyword: v || null })}
          allowClear
        />
        {(filters.provider || filters.status || filters.workerId || filters.workspaceId || filters.keyword || filters.dateFrom) && (
          <Button
            size="small"
            onClick={() => {
              setDraftKeyword('')
              patchParams({ provider: null, status: null, worker_id: null, workspace_id: null, keyword: null, date_from: null, date_to: null })
            }}
          >
            清除筛选
          </Button>
        )}
      </div>

      {view === 'list' ? (
        <AIChatTaskListTable
          loading={listLoading}
          error={listError}
          items={listData?.items ?? []}
          total={listData?.total ?? 0}
          page={page}
          pageSize={pageSize}
          sort={sort}
          sortDir={sortDir}
          selectedExecutionId={selectedExecutionId}
          onPageChange={handleTablePageChange}
          onSelectExecution={selectRow}
          onReload={reloadAll}
        />
      ) : (
        <ConversationAggregateTable
          loading={aggLoading}
          error={aggError}
          data={aggData}
          page={page}
          pageSize={pageSize}
          onSelectExecution={selectRow}
          selectedExecutionId={selectedExecutionId}
          onPageChange={(p, ps) => patchParams({ page: String(p), page_size: String(ps) }, { resetPage: false })}
        />
      )}

      <AIChatExecutionDetailDrawer
        open={!!selectedExecutionId}
        executionId={selectedExecutionId}
        detail={execDetail}
        loading={execDetailLoading}
        error={execDetailError}
        actionBusy={actionBusy}
        onClose={closeDetailDrawer}
        onCancel={handleCancel}
        onRetry={handleRetry}
      />
    </div>
  )
}

// ============================================================ Conversation Aggregate

function AggregateStatusChip({ status, label }: { status: string; label: string }) {
  return <StatusBadge status={status} label={label} />
}

function ConversationAggregateTable({
  loading,
  error,
  data,
  page,
  pageSize,
  onSelectExecution,
  selectedExecutionId,
  onPageChange,
}: {
  loading: boolean
  error: string | null
  data: { items: AIChatAdminConversationAggregateRow[]; total: number } | null
  page: number
  pageSize: number
  onSelectExecution: (id: string) => void
  selectedExecutionId: string | null
  onPageChange: (page: number, pageSize: number) => void
}) {
  const columns: TableColumnsType<AIChatAdminConversationAggregateRow> = [
    { title: 'Conversation ID', dataIndex: 'conversation_id', width: 110, render: (v: string) => <IdCell id={v} /> },
    { title: 'Message Summary', dataIndex: 'message_summary', width: 260, ellipsis: true, render: (v: string) => <span title={v}>{v || '—'}</span> },
    { title: '聚合状态', dataIndex: 'aggregate_label', width: 160, render: (v: string) => <Tag>{v}</Tag> },
    {
      title: 'Qwen / DeepSeek / ChatGPT',
      render: (_: unknown, row) => (
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {row.executions.map((ex) => (
            <span
              key={ex.id}
              onClick={(e) => {
                e.stopPropagation()
                onSelectExecution(ex.id)
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                cursor: 'pointer',
                padding: '2px 6px',
                borderRadius: 4,
                background: ex.id === selectedExecutionId ? 'var(--pf-primary-soft)' : undefined,
              }}
            >
              <ProviderBadge provider={ex.provider} label={ex.provider_label} />
              <AggregateStatusChip status={ex.status} label={ex.status} />
            </span>
          ))}
        </span>
      ),
    },
    { title: 'Created At', dataIndex: 'created_at', width: 150, render: (v: string | null) => formatDateTime(v) },
  ]

  return (
    <div style={{ background: platformColors.surface, border: `1px solid ${platformColors.border}`, borderRadius: 8, overflow: 'hidden' }}>
      {error ? (
        <div style={{ padding: 24 }}>
          <Empty description={error} />
        </div>
      ) : (
        <Table
          className="pf-data-table"
          rowKey="message_id"
          size="small"
          loading={loading}
          dataSource={data?.items ?? []}
          columns={columns}
          scroll={{ x: 900 }}
          pagination={{
            current: page,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total) => `共 ${total} 条`,
            onChange: onPageChange,
          }}
        />
      )}
    </div>
  )
}
